import { monsterAttackRangeFeet, resolveMonsterAttackRange } from '../../../shared/rules/weaponRange.mjs';
import { getAttackRollMode } from './attackResolution';
import { minimumFootprintDistanceFeet } from './pushGeometry';
import { getAttackCover } from './weaponRangeAttack';
import { describeTarget, targetTone } from './targetingPreview';

/** Outlines for monster attack actions: each melee reach, and each ranged normal and long range, once each. */
export const monsterAttackRings = (actions = []) => {
  const rings = new Map();
  for (const action of actions) {
    const { melee, ranged, reach, range } = monsterAttackRangeFeet(action);
    if (melee) rings.set(`reach-${reach}`, { feet: reach, kind: 'reach' });
    if (ranged && range) {
      rings.set(`normal-${range.normal}`, { feet: range.normal, kind: 'normal' });
      if (range.long > range.normal) rings.set(`long-${range.long}`, { feet: range.long, kind: 'long' });
    }
  }
  return [...rings.values()].sort((a, b) => a.feet - b.feet);
};

/**
 * The DM's range preview for a monster's stat-block attack while choosing its target: the same reach/range, roll mode
 * and Half Cover the attack itself uses (ZombiesDM's enemy attack loop). A multiattack previews its first action that
 * can reach the target. Null when nothing is being targeted or the action has no reach or range in its text.
 */
export const buildEnemyTargetingPreview = ({ targeting, combatState, spatialContext }) => {
  if (targeting?.status !== 'selecting-target' || targeting.enemySpell) return null;
  const actions = (Array.isArray(targeting.actions) && targeting.actions.length ? targeting.actions : [targeting.action]).filter(Boolean);
  const sourceId = String(targeting.sourceCombatantId || '');
  const rings = monsterAttackRings(actions);
  if (!sourceId || !rings.length) return null;
  const tokens = spatialContext?.tokens || {};
  const attacker = targeting.attacker || tokens[sourceId] || {};
  return { sourceId, rings, evaluate: (targetId) => {
    const target = tokens[targetId] || {};
    const distanceFeet = tokens[sourceId] && tokens[targetId] ? minimumFootprintDistanceFeet({ sourceToken: tokens[sourceId], targetToken: tokens[targetId],
      sourceSize: attacker.size || tokens[sourceId].size, targetSize: target.size, map: spatialContext?.map }) : null;
    const checks = actions.map((action) => ({ action, range: resolveMonsterAttackRange({ action, distanceFeet }) }));
    const { action, range } = checks.find((check) => check.range.allowed) || checks[0];
    const inRange = range.allowed || Boolean(targeting.opportunityId);
    const attack = { id: `${targeting.attackId || sourceId}:preview`, name: action?.name || 'Action', attackMode: range.mode,
      longRange: !targeting.opportunityId && range.longRange, isMeleeAttack: range.mode === 'melee' };
    const rollMode = getAttackRollMode({ attackerId: sourceId, targetId, attack, attacker, target, combatState, spatialContext });
    const cover = getAttackCover({ attackerId: sourceId, targetId, spatialContext, combatState });
    const tone = targetTone({ inRange, mode: rollMode.mode, cover });
    const sources = rollMode.mode === 'advantage' ? rollMode.advantageSources : rollMode.mode === 'disadvantage' ? rollMode.disadvantageSources : [];
    return { tone, description: describeTarget({ tone, sources: sources || [], reason: range.reason, cover }) };
  } };
};
